import { Button, Form, message, Select, Space, Tag } from "antd";
import ColorHash from "color-hash";
import { object } from "prop-types";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useUpdateUser, USERS_PATH } from "../../api/users";

const colorHash = new ColorHash();

// Most frequently used states in testing, pinned to the top in this order
const PINNED_USER_STATES = ["done", "order_review"];

const getSortRank = userState => {
  const pinnedIndex = PINNED_USER_STATES.indexOf(userState);
  return pinnedIndex === -1 ? PINNED_USER_STATES.length : pinnedIndex;
};

function UserStateForm({ user, allUserStates }) {
  const [selectedUserState, setSelectedUserState] = useState(null);
  const queryClient = useQueryClient();
  const updateUser = useUpdateUser();
  const manager = useSelector(state => state.auth?.user?.username);

  const options = Object.values(allUserStates || {})
    .sort(
      (a, b) =>
        getSortRank(a.userState) - getSortRank(b.userState) ||
        a.priority - b.priority
    )
    .map(({ userState, dnd }) => ({
      value: userState,
      label: (
        <>
          {userState} {dnd && <Tag>dnd</Tag>}
        </>
      ),
    }));

  const onUpdate = () => {
    updateUser.mutate(
      {
        userId: user.userId,
        body: {
          userState: selectedUserState,
          userStateUpdateMetadata: {
            message: "Test UI user state update",
            manager,
          },
        },
        // Bypass priority/DND rules so we can move to any state, including backwards
        params: { forceUserState: true },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(USERS_PATH);
          message.success(`User state updated to ${selectedUserState}`);
          setSelectedUserState(null);
        },
        onError: err => {
          message.error(err?.message || "An error occurred");
        },
      }
    );
  };

  return (
    <Form.Item label="User State">
      <Space wrap>
        <Tag color={colorHash.hex(user.userState)}>{user.userState}</Tag>
        <Select
          showSearch
          style={{ minWidth: "240px" }}
          filterOption={(input, option) =>
            option.value.toLowerCase().includes(input.toLowerCase())
          }
          placeholder="Select a new user state"
          options={options}
          value={selectedUserState}
          onChange={setSelectedUserState}
        />
        <Button
          type="primary"
          loading={updateUser.isLoading}
          disabled={!selectedUserState || selectedUserState === user.userState}
          onClick={onUpdate}
        >
          Update State
        </Button>
      </Space>
    </Form.Item>
  );
}

UserStateForm.propTypes = {
  user: object,
  allUserStates: object,
};

export default UserStateForm;
